import type { BookingProposalListApiData } from '@navio/nsdk';
import { CustomerGroupType, IamGroupType, OrganizationType, UserGroupCarRole } from '@navio/nsdk';
import { filterUnitFor, useFilterUnits } from 'noddi-ui-web';
import {
  BOOKING_PROPOSAL_STATUS_OPTIONS,
  BOOKING_PROPOSAL_TYPE_OPTIONS,
  CUSTOMER_SEGMENT_OPTIONS,
  SALES_ITEM_CATEGORY_OPTIONS
} from 'noddi-util';
import { useAccountOptions } from '../Account/useAccountFilter';
import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { UPDATED_AT_FILTER_TEXT } from '../Date/useUpdatedAtFilter';
import { useMembershipProgramOptions } from '../MembershipPrograms/useMembershipProgramsFilter';
import { ORGANIZATION_FILTER_TEXT, useOrganizationOptions } from '../Organization/useOrganizationFilter';
import { SERVICE_CATEGORY_FILTER_TEXT, useServiceCategoryOptions } from '../Other/useServiceCategoriesFilter';
import { SALES_ITEM_CATEGORY_FILTER_TEXT } from '../SalesItem/useSalesItemCategoriesFilter';
import { useSalesItemOptions } from '../SalesItem/useSalesItemsFilter';
import {
  SERVICE_AREA_FILTER_TEXT,
  useServiceAreaOptions
} from '../ServiceDepartment/useServiceDepartmentServiceAreasFilter';
import { CUSTOMER_GROUP_TYPE_FILTER_OPTIONS } from '../UserGroup/useCustomerGroupTypeFilter';
import { COHORT_FILTER_TEXT, useUserGroupCohortOptions } from '../UserGroup/useUserGroupCohortFilter';
import { useUserGroupOptions } from '../UserGroup/useUserGroupFilter';
import { BOOKING_LOCATION_TYPE_FILTER_OPTIONS } from './useBookingLocationTypeFilter';
import { PAYMENT_OPTION_FILTER_OPTIONS } from './usePaymentOptionsFilter';

type BookingProposalQuery = Omit<
  NonNullable<BookingProposalListApiData['query']>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

const proposalFilterUnit = filterUnitFor<BookingProposalQuery>();

const STATIC_FILTERS = {
  types: proposalFilterUnit.multiSelect('types', {
    title: 'Booking proposal type',
    options: BOOKING_PROPOSAL_TYPE_OPTIONS,
    exclude: true
  }),
  statuses: proposalFilterUnit.multiSelect('statuses', {
    title: 'Proposal status',
    options: BOOKING_PROPOSAL_STATUS_OPTIONS,
    exclude: true
  }),
  customerGroupTypes: proposalFilterUnit.multiSelect('customerGroupTypes', {
    title: 'Customer Group Types',
    description: 'Filter by customer group type',
    options: CUSTOMER_GROUP_TYPE_FILTER_OPTIONS,
    defaults: [CustomerGroupType.PERSONAL, CustomerGroupType.GROUP, CustomerGroupType.ORGANIZATION],
    exclude: true
  }),
  segments: proposalFilterUnit.multiSelect('segments', {
    title: 'Customer segment',
    description:
      'Lifecycle and loyalty segments: prospects (no bookings), new (1 completed), active clients (2-9), VIP (10+ or high spend), at risk / churned by recency, and fleet (3+ vehicles). All includes everyone.',
    options: CUSTOMER_SEGMENT_OPTIONS,
    exclude: true
  }),
  locationTypes: proposalFilterUnit.multiSelect('bookingLocationTypes', {
    title: 'Location type',
    description:
      'Where the service is performed. Mobile means a worker visits the customer, stationary means the customer visits a workshop.',
    options: BOOKING_LOCATION_TYPE_FILTER_OPTIONS,
    exclude: true
  }),
  paymentOptions: proposalFilterUnit.multiSelect('paymentOptions', {
    title: 'Payment option',
    description: 'How the customer pays for the booking (e.g. pay before, pay after, invoice).',
    options: PAYMENT_OPTION_FILTER_OPTIONS,
    exclude: true
  }),
  hasMediumWrong: proposalFilterUnit.boolean('hasMediumWrong', {
    title: 'Has medium wrong',
    description: 'Filter on whether the recall has a medium wrong flag.'
  }),
  numCars: proposalFilterUnit.numberRange('numCars', {
    title: 'Number of cars',
    description: 'Filter by number of cars',
    exclude: true
  }),
  salesItemCategories: proposalFilterUnit.multiSelect('salesItemCategories', {
    ...SALES_ITEM_CATEGORY_FILTER_TEXT,
    options: SALES_ITEM_CATEGORY_OPTIONS,
    exclude: true
  }),
  createdAt: proposalFilterUnit.dateRange('createdAt', { ...CREATED_AT_FILTER_TEXT, exclude: true }),
  updatedAt: proposalFilterUnit.dateRange('updatedAt', { ...UPDATED_AT_FILTER_TEXT, exclude: true })
};

/** Admin booking proposals list. */
export const useBookingProposalFilters = () => {
  const cohorts = useUserGroupCohortOptions();
  const filters = STATIC_FILTERS;

  return useFilterUnits([
    filters.types,
    filters.statuses,
    filters.customerGroupTypes,
    // Unscoped: a department scope hides organizations with no prior bookings there (e.g. recalls).
    proposalFilterUnit.multiSelect('organizationIds', {
      ...ORGANIZATION_FILTER_TEXT,
      source: useOrganizationOptions(),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('userGroupIds', {
      title: 'Groups',
      description: 'Filter by specific groups',
      defaultVisibleOptions: 3,
      source: useUserGroupOptions({ types: [IamGroupType.GROUP] }),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('userGroupPayerIds', {
      title: 'Invoice to',
      description:
        'Invoice-to party for the booking: the booking payer, then the tire-hotel contract, then the car owner, then the customer group.',
      defaultVisibleOptions: 3,
      source: useUserGroupOptions({ types: [IamGroupType.ORGANIZATION], isInvoicePayer: true }),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('leaseholderIds', {
      title: 'Leaseholder',
      description: 'Filter by the personal customer leasing the car (leaseholder).',
      defaultVisibleOptions: 3,
      source: useUserGroupOptions({ carRoles: [UserGroupCarRole.LEASEHOLDER] }),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('leasingCompanyIds', {
      title: 'Leasing company',
      description: 'Show records whose car is owned by these leasing companies.',
      defaultVisibleOptions: 3,
      source: useOrganizationOptions({ organizationTypes: [OrganizationType.LEASING_COMPANY] }),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('carDealershipIds', {
      title: 'Car dealership',
      description: 'Show records whose car is associated with these car dealerships (selling dealer).',
      defaultVisibleOptions: 3,
      source: useOrganizationOptions({ organizationTypes: [OrganizationType.CAR_DEALERSHIP] }),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('carManagerIds', {
      title: 'Driver',
      description: 'Filter by people who are a driver or car manager for the car.',
      source: useAccountOptions(),
      exclude: true
    }),
    filters.segments,
    filters.locationTypes,
    filters.paymentOptions,
    filters.hasMediumWrong,
    filters.numCars,
    proposalFilterUnit.multiSelect('serviceAreaIds', {
      ...SERVICE_AREA_FILTER_TEXT,
      source: useServiceAreaOptions(),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('cohorts', { ...COHORT_FILTER_TEXT, source: cohorts, exclude: true }),
    proposalFilterUnit.multiSelect('salesItemIds', {
      title: 'Products',
      description: 'Filter by products',
      defaultVisibleOptions: 3,
      source: useSalesItemOptions(),
      exclude: true
    }),
    filters.salesItemCategories,
    proposalFilterUnit.multiSelect('serviceCategoryIds', {
      ...SERVICE_CATEGORY_FILTER_TEXT,
      source: useServiceCategoryOptions(),
      exclude: true
    }),
    proposalFilterUnit.multiSelect('membershipProgramIds', {
      title: 'Membership programs',
      description: 'Filter by membership programs',
      defaultVisibleOptions: 3,
      source: useMembershipProgramOptions(),
      exclude: true
    }),
    filters.createdAt,
    filters.updatedAt
  ]);
};
